/**
 * ContactSearchInput — typeahead combobox that searches across every place
 * Pollen Dots stores a person's name + dob + anniversary + phone:
 *   1) `pollen_customers` (direct lead records)
 *   2) `family_members` nested on customer records
 *   3) `group_members` (co-travellers) nested on tour records
 *
 * Why this exists: many family members are typed twice — once as a
 * co-traveller on a parent's tour, once again on a sibling's profile.
 * Selecting an existing record auto-prefills DOB / Anniversary / Phone
 * so the operator doesn't have to remember whose birthday is which.
 *
 * Props:
 *   - value: string (the visible name in the input)
 *   - onChange(name): called on every keystroke
 *   - onSelect(record): called when a search result row is picked. Receives
 *       the full record { id, name, phone, dob, anniversary, relation, notes, via }
 *   - placeholder, className, testId: standard pass-throughs
 *
 * Network: hits `GET /api/pollen/query-customers/search?q=` — already
 * built for the query workspace. Re-using it keeps the data shape and
 * dedupe rules consistent across the app.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Loader2, User, Users, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function ContactSearchInput({
  value, onChange, onSelect, placeholder = 'Search or type a name',
  className = '', testId,
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search — fires 220ms after the operator stops typing.
  useEffect(() => {
    if (!open) return;
    const q = (value || '').trim();
    if (q.length < 2) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API}/api/pollen/query-customers/search?q=${encodeURIComponent(q)}&limit=8`,
          { credentials: 'include', headers: authHeaders() },
        );
        if (r.ok) {
          const data = await r.json();
          setResults(Array.isArray(data) ? data : []);
        } else { setResults([]); }
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, open]);

  // Close the dropdown when the operator taps anywhere else on the page.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (rec) => {
    onSelect && onSelect(rec);
    setOpen(false);
  };

  // Pick the right glyph for each kind of match — helps the eye scan
  // the dropdown quickly (customer vs family vs co-traveller).
  const iconFor = (via) => {
    if (!via) return <User className="w-3 h-3 text-slate-500" />;
    if (via.startsWith('Family')) return <Heart className="w-3 h-3 text-rose-600" />;
    if (via.startsWith('Co-traveller')) return <Users className="w-3 h-3 text-indigo-600" />;
    return <User className="w-3 h-3 text-emerald-700" />;
  };

  return (
    <div className={`relative ${className}`} ref={wrapRef}>
      <Input
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="h-8 text-sm"
        data-testid={testId}
        autoComplete="off"
      />
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-40
                        bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto"
             data-testid={`${testId}-dropdown`}>
          {loading && (
            <div className="p-3 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && (value || '').trim().length >= 2 && (
            <div className="p-3 text-xs text-muted-foreground italic">
              No matches yet — keep typing to add as a new entry.
            </div>
          )}
          {!loading && results.length === 0 && (value || '').trim().length < 2 && (
            <div className="p-3 text-xs text-muted-foreground italic">
              Type at least 2 letters to search customers, family, co-travellers…
            </div>
          )}
          {results.map((r, idx) => (
            <button
              type="button"
              key={`${r.id}-${idx}`}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-b-0
                         flex items-start gap-2 transition-colors"
              data-testid={`${testId}-option-${idx}`}
            >
              <span className="mt-0.5 shrink-0">{iconFor(r.via)}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{r.name}</span>
                <span className="block text-[10px] text-muted-foreground truncate">
                  {r.via || 'Customer'}
                  {r.phone ? ` · ${r.phone}` : ''}
                  {r.dob ? ` · 🎂 ${r.dob.slice(0, 10)}` : ''}
                  {r.anniversary ? ` · 💍 ${r.anniversary.slice(0, 10)}` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
